import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { NavLink, Outlet } from "react-router-dom";
import HasPermission, { HasAnyOfThisPermissions, CanManageAbsenceRequest, CanManageCompany, CanManageEmployees, CanManageOvertime, CanManagePlatform, CanManageWhistle, CanManageWorkContracts, CanSendHisAbsenceRequest, CanViewAbsenceDailyStats, CanViewAbsenceReports, CanViewCompanyDetail, CanViewCompanyLog, CanViewHisAbsenceRequest, CanViewHisCostRefund, CanViewHisLog, CanViewHisOvertime, CanViewHisWhistle, CanManagePrivacyPolicy, CanManageCostRefund, CanGrantCriticalRoles, CanSendHealthData, CanManagePatients, CanManageHealthPolicy, CanManageSafetySettings, CanManageSafetyRequest, CanViewDevices, CanManageDevices } from "../app/PermissionChecker";
import { logOutAndResetApi, selectCurrentUserDisplayname, selectCurrentUserGuid, selectLicenseExpired } from "../features/auth/authSlice";
import LanguageSwitcher from "./LanguageSwitcher";
import Notifications from "./Notifications";
import CompanySwitch from "./CompanySwitch";
import PrivacyPolicyPopup from "features/core/privacy/components/PrivacyPolicyPopup";
import UserImage from "./commons/UserImage";
import ModuleMenu from "./commons/ModuleMenu";


const Layout = (props) => {

    const [t, i18n] = useTranslation(["global", "absenceReport", "whistle", "safety", "health", "device"])

    const currentUserDisplayName = useSelector(selectCurrentUserDisplayname)
    const currentUserGuid = useSelector(selectCurrentUserGuid)

    const licenseExpired = useSelector(selectLicenseExpired)
    const dispatch = useDispatch()

    // CORE
    const canManagePrivacyPolicy = HasPermission(CanManagePrivacyPolicy)
    const canManageCompany = HasPermission(CanManageCompany)
    const canViewCompanyDetail = HasPermission(CanViewCompanyDetail)
    const canManageEmployee = HasPermission(CanManageEmployees)
    const canGrantCriticalRoles = HasPermission(CanGrantCriticalRoles)
    const canViewCompanyLog = HasPermission(CanViewCompanyLog)

    // HR
    const canAccessMyAbsenceMenu = HasAnyOfThisPermissions([CanViewHisAbsenceRequest, CanSendHisAbsenceRequest])
    const canViewHisCostRefund = HasPermission(CanViewHisCostRefund)
    const canViewHisOvertime = HasPermission(CanViewHisOvertime)
    const canAccessAbsenceMenu = HasAnyOfThisPermissions([CanViewAbsenceDailyStats, CanManageAbsenceRequest, CanManageWorkContracts])
    const canManageOvertime = HasPermission(CanManageOvertime)
    const canManageCostRefund = HasPermission(CanManageCostRefund)
    const canManageWorkContracts = HasPermission(CanManageWorkContracts)
    const absenceReportEnabled = canAccessMyAbsenceMenu || canAccessAbsenceMenu
    const canManageAbsenceRequest = HasPermission(CanManageAbsenceRequest)
    const canViewAbsenceDailyStats = HasPermission(CanViewAbsenceDailyStats)
    const canViewHrReport = HasPermission(CanViewAbsenceReports)

    // SAFETY
    const isSafetyAvailable = HasAnyOfThisPermissions([CanManageSafetyRequest, CanManageSafetySettings])
    const canManageSafetyRequest = HasPermission(CanManageSafetyRequest)
    const canManageSafetySettings = HasPermission(CanManageSafetySettings)

    // HEALTH
    const isHealthAvailable = HasAnyOfThisPermissions([CanManagePatients, CanManageHealthPolicy])
    const canManagePatients = HasPermission(CanManagePatients)
    const canManageHealthPolicy = HasPermission(CanManageHealthPolicy)

    // DEVICES
    const isDeviceAvailable = HasAnyOfThisPermissions([CanViewDevices, CanManageDevices])
    const canManageDevices = HasPermission(CanManageDevices)

    // WHISTLE
    const canManageWhistle = HasPermission(CanManageWhistle)
    const canViewHisWhistle = HasPermission(CanViewHisWhistle)
    const whistleEnabled = (canManageWhistle || canViewHisWhistle)

    const isHRAvailable = (absenceReportEnabled || whistleEnabled)

    // ADMIN
    const isPlatformAdminAvailable = HasPermission(CanManagePlatform)




    const handleLogoutRequest = async (e) => {
        console.log("Requested Logout by Button")
        dispatch(logOutAndResetApi())
    }

    const [sideBar, setSideBar] = useState(true)

    useEffect(() => {
        if (!sideBar) {
            document.body.classList.toggle('sb-sidenav-toggled');
        }
    }, [sideBar])

    function toggle() {
        document.body.classList.toggle('sb-sidenav-toggled');
    }

    let { version } = require('../../package.json');
    let isDevMode = process.env.REACT_APP_IS_DEV == "true"
    console.log("NPM package version: " + version)


    // CORE ITEMS
    const mainMenuItems = [
        {
            to: "/dashboard/home",
            icon: "fa-solid fa-house",
            label: t("menu.home"),
            permission: true
        },
        {
            to: "/dashboard/notifications",
            icon: "fa-solid fa-bell",
            label: t("menu.notifications"),
            permission: true
        },
        {
            to: "#",
            icon: "fa-solid fa-bars-progress",
            label: t("menu.management"),
            permission: canViewCompanyDetail || canManageCompany || canManageEmployee || canGrantCriticalRoles,
            children: [
                {
                    to: "/dashboard/company",
                    icon: "fa-solid fa-landmark",
                    label: t("entity.company"),
                    permission: canManageCompany || canViewCompanyDetail
                },
                {
                    to: "/dashboard/businessUnits",
                    icon: "fas fa-building",
                    label: t("entity.businessUnits"),
                    permission: canManageCompany || canViewCompanyDetail
                },
                {
                    to: "/dashboard/employees",
                    icon: "fas fa-users",
                    label: t("entity.employees"),
                    permission: canManageEmployee
                },
                {
                    to: "/dashboard/roles",
                    icon: "fa-solid fa-user-shield",
                    label: t("entity.roles"),
                    permission: canGrantCriticalRoles
                },
                {
                    to: "/dashboard/license",
                    icon: "fa-solid fa-certificate",
                    label: t("menu.license"),
                    permission: canManageCompany
                },
                {
                    to: "/dashboard/privacy/editor",
                    icon: "fa-solid fa-handshake",
                    label: t("menu.privacyPolicy"),
                    permission: canManagePrivacyPolicy
                }
            ]
        },
        {
            to: "/dashboard/map",
            icon: "fas fa-map",
            label: t("menu.map"),
            permission: true
        },
        {
            to: "/dashboard/users",
            icon: "fas fa-users",
            label: t("entity.users"),
            permission: true
        },
        {
            to: "/dashboard/log",
            icon: "fa-regular fa-file-lines",
            label: t("menu.log"),
            permission: canViewCompanyLog
        }
    ];

    const mainSettingsItem = {
        to: "/dashboard/main/settings",
        label: "Settings",
        permission: canManageCompany
    };

    // HR ITEMS
    const hrMenuItems = [
        {
            to: "/dashboard/absences/my",
            icon: "fa-solid fa-calendar-check",
            label: t("absenceReport:menu.my_absences"),
            permission: canAccessMyAbsenceMenu
        },
        {
            to: "/dashboard/overtimes/my",
            icon: "fa-regular fa-calendar-plus",
            label: t("absenceReport:menu.my_overtimes"),
            permission: canViewHisOvertime
        },
        {
            to: "/dashboard/costRefunds/my",
            icon: "fa-solid fa-receipt",
            label: t("absenceReport:menu.my_cost_refunds"),
            permission: canViewHisCostRefund
        },
        {
            to: "#",
            icon: "fa-solid fa-calendar-minus",
            label: t("absenceReport:menu.absences"),
            permission: canAccessAbsenceMenu,
            children: [
                {
                    to: "/dashboard/absences/dashboard",
                    icon: "fa-solid fa-gauge",
                    label: t("absenceReport:menu.absence_dashboard_short"),
                    permission: canViewAbsenceDailyStats || canManageAbsenceRequest
                },
                {
                    to: "/dashboard/absences/manage",
                    icon: "fa-solid fa-inbox",
                    label: t("absenceReport:menu.absence_archive_short"),
                    permission: canManageAbsenceRequest
                },
                {
                    to: "/dashboard/work-contract/workers",
                    icon: "fa-solid fa-clock",
                    label: t("absenceReport:menu.leave_balances"),
                    permission: canManageWorkContracts
                }
            ]
        },
        {
            to: "/dashboard/overtimes",
            icon: "fa-regular fa-calendar-plus",
            label: t("absenceReport:menu.overtimes"),
            permission: canManageOvertime
        },
        {
            to: "/dashboard/costRefunds",
            icon: "fa-solid fa-receipt",
            label: t("absenceReport:menu.cost_refunds"),
            permission: canManageCostRefund
        },
        {
            to: "/dashboard/work-contract/models",
            icon: "fa-solid fa-file-signature",
            label: t("absenceReport:menu.contracts"),
            permission: canManageWorkContracts
        },
        {
            to: "/dashboard/absences/report",
            icon: "fa-solid fa-chart-simple",
            label: t("absenceReport:menu.absence_report_short"),
            permission: canViewHrReport
        },
        {
            to: "/dashboard/whistle/dashboard",
            icon: "fa-solid fa-triangle-exclamation",
            label: t("whistle:menu.warnings"),
            permission: canManageWhistle
        },
        {
            to: "/dashboard/whistle/my",
            icon: "fa-solid fa-triangle-exclamation",
            label: t("whistle:menu.my_warnings"),
            permission: canViewHisWhistle
        },
    ];

    const hrSettingItem = {
        to: "/dashboard/absences/settings",
        label: "HR Settings",
        permission: canManageAbsenceRequest
    };

    // ADMIN ITEMS
    const adminMenuItems = [
        {
            to: "/dashboard/admin/companies",
            icon: "fa-solid fa-screwdriver-wrench",
            label: "Platform Admin",
            permission: true
        }
    ]

    // HEALTH ITEMS
    const healthMenuItems = [
        {
            to: "/dashboard/health/dashboard",
            icon: "fa-solid fa-heart-pulse",
            label: t("health:menu.real_time"),
            permission: true
        },
        {
            to: "/dashboard/health/events",
            icon: "fa-solid fa-calendar-days",
            label: t("health:menu.events_log"),
            permission: true
        },
        {
            to: "/dashboard/health/history",
            icon: "fa-solid fa-chart-simple",
            label: t("health:menu.data"),
            permission: true
        },
        {
            to: "/dashboard/health/profiles",
            icon: "fa-solid fa-sliders",
            label: t("health:menu.profiles"),
            permission: canManageHealthPolicy
        },
        {
            to: "/dashboard/health/patients",
            icon: "fa-solid fa-users",
            label: t("health:menu.patients"),
            permission: canManagePatients
        }
    ]

    const healthSettingItem = {
        to: "/dashboard/health/settings",
        label: "Health Settings",
        permission: canManageHealthPolicy
    };

    // DEVICES ITEMS
    const devicesMenuItems = [
        {
            to: "/dashboard/devices/dashboard",
            icon: "fa-solid fa-house-laptop",
            label: t("device:menu.devices_manager_short"),
            permission: true
        }
    ]

    const devicesSettingItem = {
        to: "/dashboard/devices/settings",
        label: "Devices Settings",
        permission: canManageDevices
    };

    // SAFETY ITEMS
    const safetyMenuItems = [
        {
            to: "/dashboard/safety/dashboard",
            icon: "fa-solid fa-shield-heart",
            label: t("safety:menu.safety_dashboard_short"),
            permission: canManageSafetyRequest
        },
        {
            to: "/dashboard/safety/stats",
            icon: "fa-solid fa-chart-simple",
            label: t("safety:menu.report_short"),
            permission: canManageSafetyRequest
        }
    ]

    const safetySettingItem = {
        to: "/dashboard/safety/settings",
        label: "Safety Settings",
        permission: canManageSafetySettings
    };


    return (
        <main className="App sb-nav-fixed">

            <nav className="sb-topnav navbar navbar-expand navbar-dark bg-dark">

                <NavLink to="/dashboard/home" className="nav-link navbar-brand ps-3">
                    <img width="100%" src="/EffiHand_logo_nt.png" />
                </NavLink>


                <button onClick={toggle} className="btn btn-link btn-sm order-1 order-lg-0 me-4 me-lg-0" id="sidebarToggle" href="#!"><i className="fas fa-bars"></i></button>

                <CompanySwitch
                    onChange={(id) => {
                        console.log("Company Changed: " + JSON.stringify(id))
                    }}
                />

                <form className="d-none d-md-inline-block form-inline ms-auto me-0 me-md-3 my-2 my-md-0" >
                    <div className="input-group" style={{ display: 'none' }}>
                        <input className="form-control" type="text" placeholder="Search for..." aria-label="Search for..." aria-describedby="btnNavbarSearch" />
                        <button className="btn btn-primary" id="btnNavbarSearch" type="button"><i className="fas fa-search"></i></button>
                    </div>
                </form>

                <ul className="navbar-nav ms-auto ms-md-0 me-3 me-lg-4 d-flex align-items-center">
                    <Notifications />
                    <li className="nav-item dropdown">

                        <a className="nav-link dropdown-toggle" id="navbarDropdown" href="#" role="button" data-bs-toggle="dropdown" aria-expanded="false">



                            <UserImage guid={currentUserGuid} size={64} border={true} className="mt-3" />

                        </a>
                        <ul className="dropdown-menu dropdown-menu-end" aria-labelledby="navbarDropdown">
                            <li><a className="dropdown-item disabled">{currentUserDisplayName}</a></li>
                            <li><NavLink to="/dashboard/userInfo" className="dropdown-item"><i className="fas fa-user"></i> {t("menu.my_profile")}</NavLink></li>
                            <li><a className="dropdown-item disabled" href=""><i className="fa-solid fa-gear"></i> {t("menu.settings")}</a></li>
                            {
                                HasPermission(CanViewHisLog) &&
                                <li><NavLink to="/dashboard/myLog" className="dropdown-item"><i className="fa-regular fa-file-lines"></i> {t("menu.activity_log")}</NavLink></li>
                            }
                            <li><hr className="dropdown-divider" /></li>
                            <li><a className="dropdown-item">{t("title.languages")}: <br /><LanguageSwitcher /></a></li>
                            <li><hr className="dropdown-divider" /></li>
                            <li><NavLink className="dropdown-item" onClick={handleLogoutRequest} to={"/login"}><i className="fas fa-right-from-bracket"></i> {t("action.logout")}</NavLink></li>
                        </ul>
                    </li>
                </ul>
            </nav>
            <div id="layoutSidenav">
                <div id="layoutSidenav_nav">
                    <nav className="sb-sidenav accordion sb-sidenav-dark" id="sidenavAccordion">
                        <div className="sb-sidenav-menu">
                            {
                                !licenseExpired &&
                                <div className="nav">
                                    <ModuleMenu label="Core" items={mainMenuItems} settings={mainSettingsItem} />

                                    {
                                        isHRAvailable ?
                                            <ModuleMenu label="HR" items={hrMenuItems} settings={hrSettingItem} /> : null
                                    }


                                    {
                                        isSafetyAvailable ? <ModuleMenu label="SAFETY" items={safetyMenuItems} settings={safetySettingItem} /> : null
                                    }

                                    {
                                        isHealthAvailable ?
                                            <ModuleMenu label="HEALTH" items={healthMenuItems} settings={healthSettingItem} /> : null
                                    }
                                    {
                                        isDeviceAvailable ?
                                            <ModuleMenu label="DEVICES" items={devicesMenuItems} settings={devicesSettingItem} /> : null
                                    }

                                    {
                                        isPlatformAdminAvailable ?
                                            <ModuleMenu label="ADMIN" items={adminMenuItems} /> : null
                                    }


                                    {
                                        isDevMode &&
                                        <>
                                            <div className="sb-sidenav-menu-heading">DEV</div>
                                            <NavLink to="/dashboard/sandbox" className="nav-link">
                                                <div className="sb-nav-link-icon"><i className="fa-solid fa-flask"></i></div>
                                                SandBox
                                            </NavLink>

                                        </>
                                    }

                                    <div style={{ display: 'none' }}>

                                        <div className="sb-sidenav-menu-heading">Interface</div>
                                        <a className="nav-link collapsed" href="#" data-bs-toggle="collapse" data-bs-target="#collapseLayouts" aria-expanded="false" aria-controls="collapseLayouts">
                                            <div className="sb-nav-link-icon"><i className="fas fa-columns"></i></div>
                                            Layouts
                                            <div className="sb-sidenav-collapse-arrow"><i className="fas fa-angle-down"></i></div>
                                        </a>
                                        <div className="collapse" id="collapseLayouts" aria-labelledby="headingOne" data-bs-parent="#sidenavAccordion">
                                            <nav className="sb-sidenav-menu-nested nav">
                                                <a className="nav-link" href="layout-static.html">Static Navigation</a>
                                                <a className="nav-link" href="layout-sidenav-light.html">Light Sidenav</a>
                                            </nav>
                                        </div>
                                        <a className="nav-link collapsed" href="#" data-bs-toggle="collapse" data-bs-target="#collapsePages" aria-expanded="false" aria-controls="collapsePages">
                                            <div className="sb-nav-link-icon"><i className="fas fa-book-open"></i></div>
                                            Pages
                                            <div className="sb-sidenav-collapse-arrow"><i className="fas fa-angle-down"></i></div>
                                        </a>
                                        <div className="collapse" id="collapsePages" aria-labelledby="headingTwo" data-bs-parent="#sidenavAccordion">
                                            <nav className="sb-sidenav-menu-nested nav accordion" id="sidenavAccordionPages">
                                                <a className="nav-link collapsed" href="#" data-bs-toggle="collapse" data-bs-target="#pagesCollapseAuth" aria-expanded="false" aria-controls="pagesCollapseAuth">
                                                    Authentication
                                                    <div className="sb-sidenav-collapse-arrow"><i className="fas fa-angle-down"></i></div>
                                                </a>
                                                <div className="collapse" id="pagesCollapseAuth" aria-labelledby="headingOne" data-bs-parent="#sidenavAccordionPages">
                                                    <nav className="sb-sidenav-menu-nested nav">
                                                        <a className="nav-link" href="login.html">Login</a>
                                                        <a className="nav-link" href="register.html">Register</a>
                                                        <a className="nav-link" href="password.html">Forgot Password</a>
                                                    </nav>
                                                </div>
                                                <a className="nav-link collapsed" href="#" data-bs-toggle="collapse" data-bs-target="#pagesCollapseError" aria-expanded="false" aria-controls="pagesCollapseError">
                                                    Error
                                                    <div className="sb-sidenav-collapse-arrow"><i className="fas fa-angle-down"></i></div>
                                                </a>
                                                <div className="collapse" id="pagesCollapseError" aria-labelledby="headingOne" data-bs-parent="#sidenavAccordionPages">
                                                    <nav className="sb-sidenav-menu-nested nav">
                                                        <a className="nav-link" href="401.html">401 Page</a>
                                                        <a className="nav-link" href="404.html">404 Page</a>
                                                        <a className="nav-link" href="500.html">500 Page</a>
                                                    </nav>
                                                </div>
                                            </nav>
                                        </div>
                                        <div className="sb-sidenav-menu-heading">Addons</div>
                                        <a className="nav-link" href="/chart">
                                            <div className="sb-nav-link-icon"><i className="fas fa-chart-area"></i></div>
                                            Charts
                                        </a>
                                        <NavLink to="/dashboard/auz" className="nav-link">
                                            <div className="sb-nav-link-icon"><i className="fas fa-table"></i></div>
                                            Auz
                                        </NavLink>
                                    </div>
                                </div>
                            }

                        </div>
                        {/* <div className="sb-sidenav-footer">
                            <div className="small">{t("helper.logged_in_as")}</div>
                            <span className="text-eh fw-bold">{currentUserDisplayName}</span>
                        </div> */}
                    </nav>
                </div>
                <div id="layoutSidenav_content">
                    <main>
                        <Outlet />
                        <PrivacyPolicyPopup />
                    </main>
                    <footer className="py-4 bg-dark mt-auto">
                        <div className="container-fluid px-4">
                            <div className="d-flex align-items-center justify-content-between small">
                                <div className="text-muted">EffiHand® - v.{version}</div>
                                <div style={{ display: 'none' }}>
                                    <NavLink to="/dashboard/privacy">Privacy Policy</NavLink>
                                    &middot;
                                    <a href="#">Terms &amp; Conditions</a>
                                </div>
                            </div>
                        </div>
                    </footer>
                </div>
            </div>
        </main>
    )
}

export default Layout